import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { catalogService } from '../../../services/catalogService';

/**
 * Returns spread info for a given page number.
 * - Page 1: front cover (solo)
 * - Page totalPages: back cover (solo)
 * - Even pages: left of spread (canonical)
 * - Odd pages (>1, <totalPages): right of spread, canonical = pageNumber - 1
 */
const getSpreadInfo = (pageNumber, totalPages) => {
  if (pageNumber === 1) return { type: 'front', canonicalPageNum: 1 };
  if (pageNumber === totalPages) return { type: 'back', canonicalPageNum: totalPages };
  const leftPageNum = pageNumber % 2 === 0 ? pageNumber : pageNumber - 1;
  const rightPageNum = leftPageNum + 1;
  return { type: 'spread', canonicalPageNum: leftPageNum, leftPageNum, rightPageNum };
};

const PageKeywordsPanel = ({ page, pages = [], totalPages, onKeywordsUpdated }) => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Derive canonical page from spread logic
  const spreadInfo = page && totalPages ? getSpreadInfo(page.pageNumber, totalPages) : null;
  const canonicalPageNum = spreadInfo?.canonicalPageNum ?? page?.pageNumber;
  const canonicalPage = pages.find((p) => p.pageNumber === canonicalPageNum) || page;

  // Sync input when canonical page changes
  useEffect(() => {
    if (canonicalPage) {
      setInput((canonicalPage.keywords || []).join(', '));
      setSaved(false);
      setError(null);
    }
  }, [canonicalPage?.id]);

  const handleSave = async () => {
    if (!canonicalPage?.id) return;
    const keywords = input
      .split(',')
      .map((k) => k.trim().toLowerCase())
      .filter(Boolean);

    setSaving(true);
    setError(null);
    try {
      await catalogService.updatePageKeywords(canonicalPage.id, keywords);
      onKeywordsUpdated?.(canonicalPage.id, keywords);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err?.message || t('pageKeywords.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const keywords = input
    .split(',')
    .map((k) => k.trim().toLowerCase())
    .filter(Boolean);

  const getSpreadLabel = () => {
    if (!spreadInfo) return t('pageKeywords.pageLabel', { number: page?.pageNumber });
    if (spreadInfo.type === 'front') return t('pageKeywords.frontCover');
    if (spreadInfo.type === 'back') return t('pageKeywords.backCover');
    return t('pageKeywords.spreadLabel', { left: spreadInfo.leftPageNum, right: spreadInfo.rightPageNum });
  };

  if (!page) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        {t('pageKeywords.selectPage')}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Page indicator */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon name="FileText" size={13} />
        <span>{getSpreadLabel()}</span>
      </div>

      {/* Textarea */}
      <div>
        <label className="block text-xs font-medium text-foreground mb-1.5">
          {t('pageKeywords.label')}
        </label>
        <textarea
          value={input}
          onChange={(e) => { setInput(e.target.value); setSaved(false); }}
          placeholder={t('pageKeywords.placeholder')}
          rows={4}
          className="w-full text-sm bg-background border border-border rounded-md px-3 py-2 text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <p className="text-xs text-muted-foreground mt-1">
          {t('pageKeywords.hint')}
        </p>
      </div>

      {/* Preview tags */}
      {keywords.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {keywords.map((kw) => (
            <span
              key={kw}
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-accent/10 text-accent text-xs"
            >
              {kw}
            </span>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-1.5 text-xs text-destructive">
          <Icon name="AlertCircle" size={12} />
          {error}
        </div>
      )}

      {/* Save button */}
      <Button
        variant="default"
        size="sm"
        className="w-full"
        onClick={handleSave}
        loading={saving}
        disabled={saving}
        iconName={saved ? 'Check' : 'Save'}
        iconPosition="left"
      >
        {saved ? t('pageKeywords.saved') : t('pageKeywords.save')}
      </Button>
    </div>
  );
};

export default PageKeywordsPanel;
